import { faLink, faLinkSlash } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { Editor } from '@tiptap/react'
import { useEditorState } from '@tiptap/react'
import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'
import {
  editorButtonToggle,
  editorButtonToggleActive,
  editorLinkAction,
  editorLinkInput,
  editorLinkPopover
} from './EditorInput.css'

export const LinkButton = ({ editor }: { editor: Editor }) => {
  const [open, setOpen] = useState(false)
  const [url, setUrl] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  const editorState = useEditorState({
    editor,
    selector: ({ editor }) => ({
      isLink: editor.isActive('link'),
      href: editor.getAttributes('link').href as string | undefined
    })
  })

  useEffect(() => {
    if (open) {
      setUrl(editorState.href ?? '')
    }
  }, [open, editorState.href])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const applyLink = () => {
    const hasScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(url)
    const href = url && !hasScheme ? `https://${url}` : url
    if (!href) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href }).run()
    }
    setOpen(false)
  }

  const removeLink = () => {
    editor.chain().focus().unsetLink().run()
    setOpen(false)
  }

  return (
    <div ref={ref}>
      <button
        type="button"
        className={clsx(
          editorButtonToggle,
          editorState.isLink && editorButtonToggleActive
        )}
        onClick={() => setOpen((v) => !v)}
      >
        <FontAwesomeIcon icon={faLink} />
      </button>
      {open && (
        <div className={editorLinkPopover}>
          <input
            className={editorLinkInput}
            type="url"
            placeholder="https://..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyLink()
              if (e.key === 'Escape') setOpen(false)
            }}
          />
          <button
            type="button"
            className={editorLinkAction}
            onClick={applyLink}
          >
            <FontAwesomeIcon icon={faLink} />
          </button>
          {editorState.isLink && (
            <button
              type="button"
              className={editorLinkAction}
              onClick={removeLink}
            >
              <FontAwesomeIcon icon={faLinkSlash} />
            </button>
          )}
        </div>
      )}
    </div>
  )
}
